import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api } from "../../api/client";
import { useDialogs } from "../../components/Dialogs";
import { Icon } from "../../components/Icon";
import { RelativeTime } from "../../components/RelativeTime";
import { ConnectedApps } from "./ConnectedApps";

const TOKENS_KEY = ["api-tokens"] as const;

/** Personal access tokens for the board MCP endpoint (AI assistants acting as you). */
export function AssistantAccess() {
  const qc = useQueryClient();
  const { confirm } = useDialogs();
  const [name, setName] = useState("");
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);
  const tokens = useQuery({ queryKey: TOKENS_KEY, queryFn: api.apiTokens });
  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.createApiToken(name.trim()),
    onSuccess: (t) => {
      setCreated({ name: t.name, token: t.token });
      setName("");
      qc.invalidateQueries({ queryKey: TOKENS_KEY });
    },
  });
  const revoke = useMutation({
    mutationFn: api.revokeApiToken,
    onSettled: () => qc.invalidateQueries({ queryKey: TOKENS_KEY }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };
  const url = `${window.location.origin}/mcp/board`;

  return (
    <div className="form-stack assistant-access">
      <h3 className="section-label">AI assistant access (MCP)</h3>
      <p className="muted small">
        Connect Claude Code, Claude Desktop or another MCP client to this board. With your permissions it can read
        cards, edit titles, descriptions and acceptance criteria, comment, attach files, and create cards in Task
        Definition. It cannot start work, move cards or approve reviews. What it does appears under the token's name,
        as an assistant acting for you.
      </p>
      <div className="field">
        <span className="field-label">Server URL</span>
        <CopyField value={url} />
      </div>
      <ConnectedApps />
      <span className="field-label">Personal access tokens</span>
      <p className="muted small">For clients that take a header instead of signing in (Claude Code, Claude Desktop via mcp-remote, scripts).</p>

      {created ? (
        <NewToken name={created.name} token={created.token} url={url} onDone={() => setCreated(null)} />
      ) : (
        <form className="field-row token-create" onSubmit={submit}>
          <label className="field">
            <span className="field-label">New token</span>
            <input
              className="input"
              placeholder="Assistant name shown on its changes, e.g. Claude Desktop"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <button className="btn btn-primary" disabled={!name.trim() || create.isPending}>
            Create token
          </button>
        </form>
      )}
      {create.error && <div className="error-box">{create.error.message}</div>}

      {tokens.data && tokens.data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Created</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.data.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="muted small">
                  <RelativeTime iso={t.created_at} />
                </td>
                <td className="muted small">{t.last_used_at ? <RelativeTime iso={t.last_used_at} /> : "never"}</td>
                <td className="table-actions">
                  <button
                    className="btn btn-sm btn-ghost-danger"
                    disabled={revoke.isPending}
                    onClick={async () => {
                      if (await confirm({ title: `Revoke “${t.name}”?`, body: "Assistants using it lose access immediately.", confirmLabel: "Revoke", danger: true }))
                        revoke.mutate(t.id);
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewToken({ name, token, url, onDone }: { name: string; token: string; url: string; onDone: () => void }) {
  const claudeCode = `claude mcp add --transport http agent-task-runner ${url} --header "Authorization: Bearer ${token}"`;
  const desktop = JSON.stringify(
    {
      mcpServers: {
        "agent-task-runner": {
          command: "npx",
          args: ["-y", "mcp-remote", url, "--header", "Authorization:${ATR_TOKEN}"],
          env: { ATR_TOKEN: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  );
  return (
    <div className="token-new">
      <p className="small">
        <strong>Token “{name}” created.</strong> Copy it now; it is not shown again.
      </p>
      <CopyField value={token} mono />
      <div className="field">
        <span className="field-label">Claude Code</span>
        <CopyField value={claudeCode} mono multiline />
      </div>
      <div className="field">
        <span className="field-label">Claude Desktop (claude_desktop_config.json, needs Node.js)</span>
        <CopyField value={desktop} mono multiline />
      </div>
      <p className="muted small">
        Other clients: Streamable HTTP at the server URL with the header <code className="code-inline">Authorization: Bearer &lt;token&gt;</code>.
      </p>
      <div className="row-actions row-actions-start">
        <button className="btn" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

function CopyField({ value, mono, multiline }: { value: string; mono?: boolean; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked: the text is selectable */
    }
  };
  return (
    <div className="copy-field">
      {multiline ? (
        <pre className={mono ? "copy-value mono" : "copy-value"}>{value}</pre>
      ) : (
        <input className={mono ? "input input-mono" : "input"} readOnly value={value} onFocus={(e) => e.target.select()} />
      )}
      <button type="button" className="btn btn-sm" onClick={copy} title="Copy">
        <Icon name={copied ? "check" : "file"} size={13} /> {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
